import { ButtonProps } from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import * as Yup from "yup";
import { useCallback, useMemo } from "react";
import { useMutation } from "react-query";
import { Dialog } from "../../../components/Dialog";
import { useToast } from "../../../hooks/useToast";
import { deletePerson } from "../../../services/person";
import { Person } from "../../../types/person";
import { useFormik } from "formik";

type FormValues = {
  reason?: string;
  document?: string;
};

type DeletePersonProps = {
  person: Person;
  open: boolean;
  onClose: () => void;
  onGoBack: () => void;
};

const validationSchema = Yup.object().shape({
  reason: Yup.string().required("Motivo é obrigatório"),
  document: Yup.number().required("Documento é obrigatório"),
});

export function DeletePerson({
  person,
  open,
  onClose,
  onGoBack,
}: DeletePersonProps) {
  const toast = useToast();

  const removePerson = useMutation(
    async (data: FormValues) => {
      const response = await deletePerson(data);
      return response;
    },
    {
      onSuccess: () => {
        toast.show("Cliente removido com sucesso!", { severity: "success" });
        handleClose();
        onGoBack();
      },
      onError: () => {
        toast.show("Erro ao remover o cliente!", { severity: "error" });
      },
    }
  );

  const handleSave = useCallback(
    async (data) => {
      await removePerson.mutateAsync({ ...data, id: person?.id_pessoa });
    },
    [removePerson, person]
  );

  const formik = useFormik<FormValues>({
    initialValues: {} as FormValues,
    validationSchema,
    validateOnChange: false,
    onSubmit: handleSave,
  });

  const handleClose = useCallback(() => {
    onClose();
    formik.resetForm();
  }, [formik, onClose]);

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        disabled: formik.isSubmitting,
        children: "Voltar",
        onClick: handleClose,
      },
      {
        disabled: formik.isSubmitting,
        children: formik.isSubmitting ? "Enviado solicitação" : "Deletar",
        variant: "contained",
        onClick: () => formik.handleSubmit(),
      },
    ];
  }, [formik, handleClose]);

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      title={`Deletar ${person?.nome}`}
      open={open}
      actions={actions}
      onClose={handleClose}
    >
      <Stack paddingY={2} spacing={4}>
        <TextField
          autoFocus
          label="Documento"
          onChange={({ target: { value } }) =>
            formik.setFieldValue("document", value)
          }
          fullWidth
          size="small"
          value={formik.values.document}
          error={Boolean(formik.errors.document)}
          helperText={formik.errors.document}
        />
        <TextField
          rows={5}
          multiline
          label="Motivo da exclusão"
          onChange={({ target: { value } }) =>
            formik.setFieldValue("reason", value)
          }
          fullWidth
          size="small"
          value={formik.values.reason}
          error={Boolean(formik.errors.reason)}
          helperText={formik.errors.reason}
        />
      </Stack>
    </Dialog>
  );
}
